import React from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import LoginPage from './pages/LoginPage'
import RegistrationPage from './pages/RegistrationPage'
import NewUserRegistrationPage from './pages/NewUserRegistrationPage'
import SuperAdminRegistrationPage from './pages/SuperAdminRegistrationPage'
import SuperAdminLoginPage from './pages/SuperAdminLoginPage'
import OTPPage from './pages/OTPPage'
import SuperAdminDashboard from './pages/SuperAdminDashboard'
import SuperAdminNotifications from './pages/SuperAdminNotifications'
import SAdminDashboardPage from './pages/SAdminDashboardPage'
import HospitalDashboard from './pages/HospitalDashboard'
import FleetDashboard from './pages/FleetDashboard'
import CommandCenterDashboard from './pages/CommandCenterDashboard'
import AmbulanceBookingPage from './pages/AmbulanceBookingPage'
import AmbulanceInstantBooking from './pages/AmbulanceInstantBooking'
import AmbulanceInstantBookingDuplicate from './pages/AmbulanceInstantBookingDuplicate'
import AmbulanceShowcase from './pages/AmbulanceDemo'
import AnalyticsDashboard from './components/dashboard/AnalyticsDashboard'
import ApiIntegrationExample from './components/examples/ApiIntegrationExample'
import FirstTimePasswordResetPage from './pages/FirstTimePasswordResetPage'
import ProtectedRoute from './components/ProtectedRoute'
import RoleBasedRedirect from './components/RoleBasedRedirect'
import './index.css'
import BookingTrackingPage from './pages/BookingTrackingPage'
import { getDashboardTypeFromPort, shouldRedirectUser, getExpectedDashboardForRole } from './utils/portUtils';
import FleetPartnerHospitals from './pages/FleetPartnerHospitals'
import HospitalPartnershipRequests from './pages/HospitalPartnershipRequests'
import SuperAdminApprovalsQueue from './pages/SuperAdminApprovalsQueue'
import InviteAcceptancePage from './pages/InviteAcceptancePage'
import PaymentTestPage from './pages/PaymentTestPage'
import NoDashboardAccess from './pages/NoDashboardAccess'
import { HospitalOnboardingProvider } from './contexts/HospitalOnboardingContext'
import HospitalOnboardingEntryPage from './pages/hospitalOnboarding/HospitalOnboardingEntryPage'
import HospitalOnboardingLayout from './pages/hospitalOnboarding/HospitalOnboardingLayout'
import HospitalOnboardingVoicePage from './pages/hospitalOnboarding/HospitalOnboardingVoicePage'
import HospitalOnboardingSubmittedPage from './pages/hospitalOnboarding/HospitalOnboardingSubmittedPage'
import HospitalPartnerRegistrationPage from './pages/hospitalOnboarding/HospitalPartnerRegistrationPage'
import OnboardingRouteGuard from './components/hospitalOnboarding/OnboardingRouteGuard'


function App() {
  const dashboardType = getDashboardTypeFromPort();

  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LoginPage dashboardType={dashboardType} />} />
            <Route path="/login" element={<LoginPage dashboardType={dashboardType} />} />
            {/* Register route - link removed from login page, but route still accessible via direct URL */}
            <Route path="/register" element={<RegistrationPage dashboardType={dashboardType} />} />
            <Route path="/new-user-registration" element={<NewUserRegistrationPage dashboardType={dashboardType} />} />
            <Route path="/super-admin-registration" element={<SuperAdminRegistrationPage dashboardType={dashboardType} />} />
            <Route path="/super-admin-login" element={<SuperAdminLoginPage dashboardType={dashboardType} />} />
            <Route path="/otp" element={<OTPPage dashboardType={dashboardType} />} />
            <Route path="/first-time-password-reset" element={<FirstTimePasswordResetPage dashboardType={dashboardType} />} />

            {/* Public Route - Ambulance Booking (No authentication required) */}
            <Route path="/ambulance" element={<AmbulanceBookingPage />} />
            {/* Public Route - Invitation Acceptance */}
            <Route path="/invite/accept" element={<InviteAcceptancePage />} />

            {/* Public Route - Ambulance Instant Booking (No authentication required) */}
            <Route path="/ambulance-instant" element={<AmbulanceInstantBooking />} />

            {/* Public Route - Ambulance Instant Booking Duplicate (No authentication required, no topbar) */}
            <Route path="/ambulance-instant-duplicate" element={<AmbulanceInstantBookingDuplicate />} />

            {/* Public Route - Payment Test Page (No authentication required) */}
            <Route path="/payment-test" element={<PaymentTestPage />} />

            {/* Hospital & Fleet Partner Registration (Public self-serve registration entry) */}
            <Route path="/hospital/register" element={
              <HospitalOnboardingProvider>
                <HospitalPartnerRegistrationPage />
              </HospitalOnboardingProvider>
            } />
            <Route path="/hospital/partner-register" element={
              <HospitalOnboardingProvider>
                <HospitalPartnerRegistrationPage />
              </HospitalOnboardingProvider>
            } />

            {/* Hospital & Fleet Onboarding Flow (Guarded by user registration session) */}
            <Route element={
              <OnboardingRouteGuard>
                <HospitalOnboardingProvider>
                  <Outlet />
                </HospitalOnboardingProvider>
              </OnboardingRouteGuard>
            }>
              <Route path="/hospital/onboarding" element={<HospitalOnboardingEntryPage />} />
              <Route path="/hospital/onboarding/manual" element={<HospitalOnboardingLayout />} />
              <Route path="/hospital/onboarding/voice" element={<HospitalOnboardingVoicePage />} />
              <Route path="/hospital/onboarding/submitted" element={<HospitalOnboardingSubmittedPage />} />
            </Route>

            {/* Protected Routes - Admin Dashboard (Super Admin Only) */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <SuperAdminDashboard dashboardType={dashboardType} />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Super Admin Notifications */}
            <Route path="/super-admin/notifications" element={
              <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <SuperAdminNotifications />
              </ProtectedRoute>
            } />

            {/* Protected Routes - S-Admin Dashboard (Super Admin Only) */}
            <Route path="/s-admin" element={
              <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <SAdminDashboardPage />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Super Admin Approvals Queue */}
            <Route path="/admin/approvals" element={
              <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <SuperAdminApprovalsQueue />
              </ProtectedRoute>
            } />
            <Route path="/super-admin/approvals" element={
              <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <SuperAdminApprovalsQueue />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Hospital Dashboard (Hospital Users + Super Admin) */}
            <Route path="/hospital" element={
              <ProtectedRoute requiredRoles={['HOSPITAL_USER', 'SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <HospitalDashboard dashboardType={dashboardType} />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Hospital Partnership Requests */}
            <Route path="/hospital/partnerships/requests" element={
              <ProtectedRoute requiredRoles={['HOSPITAL_USER', 'SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <HospitalPartnershipRequests />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Fleet Dashboard (Tenant Admins and Users + Fleet Admin + Super Admin) */}
            <Route path="/fleet" element={
              <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'TENANT_USER', 'FLEET_ADMIN', 'SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <FleetDashboard dashboardType={dashboardType} />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Fleet Partner Hospitals Management */}
            <Route path="/fleet/partners/hospitals" element={
              <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'TENANT_USER', 'FLEET_ADMIN', 'SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <FleetPartnerHospitals />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Command Center Dashboard (Command Center Users + Super Admin) */}
            <Route path="/command-center" element={
              <ProtectedRoute requiredRoles={['COMMAND_CENTRE_USER', 'SUPER_ADMIN', 'SUPER_ADMIN_USER']}>
                <CommandCenterDashboard dashboardType={dashboardType} />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Booking Tracking */}
            <Route path="/track-booking" element={
              <ProtectedRoute>
                <BookingTrackingPage />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Analytics Dashboard (Admin Level Access) */}
            <Route path="/analytics" element={
              <ProtectedRoute requiredPermissionLevel={5}>
                <AnalyticsDashboard />
              </ProtectedRoute>
            } />

            {/* Protected Routes - Ambulance Showcase (Component Demo) */}
            <Route path="/ambulance-showcase" element={
              <ProtectedRoute>
                <AmbulanceShowcase />
              </ProtectedRoute>
            } />

            {/* Protected Routes - API Integration Example */}
            <Route path="/api-example" element={
              <ProtectedRoute>
                <ApiIntegrationExample />
              </ProtectedRoute>
            } />


            {/* Default Dashboard Route - Redirects based on user role */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <RoleBasedRedirect dashboardType={dashboardType} />
              </ProtectedRoute>
            } />

            {/* Shown for a logged-in user whose role has no dashboard mapped yet */}
            <Route path="/no-dashboard-access" element={
              <ProtectedRoute>
                <NoDashboardAccess />
              </ProtectedRoute>
            } />

            {/* Catch all route - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  )
}

export default App
